import React, { useState, FunctionComponent } from "react";
import {
  Button,
  Popover,
  Paper,
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  IconButton,
  ListItemSecondaryAction,
} from "@mui/material";
import { Edit, FilterList, Publish } from "@mui/icons-material";
import { StoredView } from "./types";

interface SelectViewButtonProps {
  storedViews: StoredView[];
  fieldGroups: any;
  handleApplyView: (view: StoredView) => void;
  handleApplyGroup: (group: any) => void;
  handleConvertGroup: (group: any) => void;
  handleOpenEditViewsDialog: () => void;
  handleResetVisibleFilterTerms: () => void;
}
// export const AssetList: FunctionComponent<AssetListProps> = () => {

const SelectViewButton: FunctionComponent<SelectViewButtonProps> = ({
  storedViews,
  fieldGroups,
  handleApplyView,
  handleApplyGroup,
  handleConvertGroup,
  handleOpenEditViewsDialog,
  handleResetVisibleFilterTerms,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [searchText, setSearchText] = useState("");

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleListItemButtonClick = (id: string): void => {
    const view = storedViews.find((view: any) => view.id === id);
    if (view) {
      handleResetVisibleFilterTerms();
      handleApplyView(view);
      handleClose();
    }
  };

  const handleGroupItemButtonClick = (id: string): void => {
    const group = fieldGroups.find((group: any) => group._id === id);
    handleResetVisibleFilterTerms();
    handleApplyGroup(group);
    handleClose();
  };

  function handleConvertGroupToView(id: string): void {
    const group = fieldGroups.find((group: any) => group._id === id);
    handleConvertGroup(group);
  }

  return (
    <div>
      <Button
        size="small"
        variant="contained"
        onClick={handleClick}
        disabled={storedViews?.length === 0 && fieldGroups?.length === 0}
        sx={{ whiteSpace: "nowrap" }}
      >
        Select View
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ width: 450, padding: "10px" }}
      >
        <Paper
          sx={{
            width: 400,
            maxHeight: "80vh",
            padding: "10px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box>
            <TextField
              placeholder="Search Views"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
          <List sx={{ overflow: "auto", flex: "1 1 auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1">Saved Views</Typography>
              <Tooltip title="Edit Views">
                <IconButton onClick={handleOpenEditViewsDialog} size="small">
                  <Edit />
                </IconButton>
              </Tooltip>
            </Box>
            {storedViews?.length === 0 ? (
              <Typography
                sx={{ marginLeft: 2, mt: 2, mb: 2, fontSize: "0.875rem" }}
              >
                Save your views and select here
              </Typography>
            ) : (
              storedViews
                ?.filter((view: any) =>
                  view.name?.toLowerCase().includes(searchText?.toLowerCase())
                )
                .map((view: { name: string; id: string }, index: number) => (
                  <ListItemButton
                    key={index}
                    onClick={() => handleListItemButtonClick(view.id)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f5f5f5", // Change background color on hover
                      },
                    }}
                  >
                    <ListItemIcon>
                      <FilterList />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        whiteSpace: "nowrap", // Prevent text from wrapping to the next line
                        overflow: "hidden", // Hide overflowed text
                        textOverflow: "ellipsis",
                      }}
                      primary={view.name}
                    />
                  </ListItemButton>
                ))
            )}

            <Box>
              <Typography variant="subtitle1">Field Groups</Typography>
            </Box>
            {fieldGroups?.length === 0 ? (
              <Typography
                sx={{ marginLeft: 2, mt: 2, mb: 2, fontSize: "0.875rem" }}
              >
                Create Field Groups and share here
              </Typography>
            ) : (
              fieldGroups
                ?.filter((group: any) =>
                  group?.name?.toLowerCase().includes(searchText?.toLowerCase())
                )
                .map((group: { name: string; _id: string }) => (
                  <ListItemButton
                    key={group._id}
                    onClick={() => handleGroupItemButtonClick(group._id)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f5f5f5", // Change background color on hover
                      },
                    }}
                  >
                    <ListItemIcon>
                      <FilterList />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        whiteSpace: "nowrap", // Prevent text from wrapping to the next line
                        overflow: "hidden", // Hide overflowed text
                        textOverflow: "ellipsis",
                      }}
                      primary={group.name}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Import field group as view">
                        <IconButton
                          edge="end"
                          onClick={() => handleConvertGroupToView(group._id)}
                        >
                          <Publish />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                ))
            )}
          </List>
        </Paper>
      </Popover>
    </div>
  );
};

export default SelectViewButton;
